import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import clsx from "clsx";
import { useRef } from "react";
import { Bubble, getElementsAtEvent, getElementAtEvent } from "react-chartjs-2";
import { ProgressGraphDataInterface } from "../ReportTypes";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const tailwindColors = ["#dc2626", "#EA580C", "#D97706", "#FFEB3B", "#65A30D"];

const ProgressScatterPlot = ({
  title,
  data,
  yAxisVariable,
  onClick,
}: {
  title: string;
  data: ProgressGraphDataInterface[];
  yAxisVariable: string;
  onClick?: (val: string[]) => void;
}) => {
  const chartRef = useRef();
  const chartData = {
    data: {
      labels: data.map((item: any) => item.label),
      datasets: [
        {
          label: title,
          data: data.map((entry: any) => ({
            x: entry.data.problemsPerStudent,
            y: entry.data[yAxisVariable],
            r: 7, //radius (size) of each point
          })),
          backgroundColor: data.map((entry: any) => {
            return getColor(
              entry.data[yAxisVariable],
              yAxisVariable === "flu" ? 20 : 100
            );
          }),
        },
      ],
    },
  };

  const options = {
    aspectRatio: 3,
    scales: {
      x: {
        min: 0,
        title: {
          display: true,
          text: "Number of Data Points per Student",
        },
        layout: {
          padding: 24,
        },
      },
      y: {
        offset: true,
        title: {
          display: true,
          text: yAxisVariable === "flu" ? "Fluency" : "Percentage",
        },
        min: 0,
        max: yAxisVariable === "flu" ? 20 : 100,
      },
    },
    plugins: {
      title: {
        display: true,
        text: title,
      },
      legend: {
        display: false,
      },
      autocolors: true,
      tooltip: {
        callbacks: {
          label: (context: { dataIndex: number; parsed: { y: any } }) => [
            data[context.dataIndex].label,
            clsx(
              yAxisVariable === "flu"
                ? `Fluency: ${context.parsed.y}`
                : `${context.parsed.y}%`
            ),
          ],
        },
      },
    },
    responsive: true,
    layout: {
      autoPadding: true,
    },
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
      }}
      className="chart-container mx-auto"
    >
      {chartData?.data && (
        <>
          <Bubble
            id="chart"
            ref={chartRef}
            data={chartData.data}
            options={options}
            onClick={(e) => {
              if (
                chartRef.current &&
                getElementsAtEvent(chartRef.current, e).length
              ) {
                const activeElement = getElementAtEvent(chartRef.current, e);

                const pointData = {
                  x: chartData.data.datasets[activeElement[0].datasetIndex]
                    .data[activeElement[0].index].x,
                  y: chartData.data.datasets[activeElement[0].datasetIndex]
                    .data[activeElement[0].index].y,
                };

                const selectedPointArr = checkForDuplicate(
                  pointData,
                  chartData.data.datasets[0].data
                );

                const skArr = selectedPointArr.map(
                  (index: number) => chartData.data.labels[index]
                );
                if (onClick) onClick(skArr);
              }
            }}
          />
        </>
      )}
    </div>
  );
};

export default ProgressScatterPlot;

const checkForDuplicate = (
  pointData: { x: number; y: number },
  dataset: { x: number; y: number }[]
) => {
  const indices = [];
  for (let i = 0; i < dataset.length; i++) {
    if (dataset[i].x === pointData.x && dataset[i].y === pointData.y) {
      indices.push(i);
    }
  }
  return indices;
};

const getColor = (value: any, quotient: any) => {
  if (value === 0 || quotient === 0) return "#dc2626";
  let index = Math.floor((value / quotient) * tailwindColors.length);
  if (index > 4) index = index - 1;
  return tailwindColors[index];
};
